import { create } from "zustand";
import { queryClient } from "@/lib/query/queryClient";
import type { ActiveAccountContext, FunctionalAccount, FunctionalAccountType, IdentityContext } from "@/features/auth/types";
import type { BusinessRole, ProfileRole, SystemRole } from "@/types/roles";

const REFRESH_TOKEN_KEY = "eshendetesia.refresh_token";

export interface AuthUser {
  id: string;
  accountId: string;
  systemRole: SystemRole;
  profileRole: ProfileRole | null;
  businessRole?: BusinessRole | null;
  verified: boolean;
  permissions: string[];
  email?: string;
  fullName?: string;
  avatarUrl?: string | null;
  patientId?: string | null;
  clinicianId?: string | null;
  isImpersonating?: boolean;
  impersonatedBy?: string | null;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthStoreState {
  accessToken: string | null;
  user: AuthUser | null;
  identity: IdentityContext | null;
  availableFunctionalAccounts: FunctionalAccount[];
  activeFunctionalAccount: FunctionalAccount | null;
  activeOrganizationContext: ActiveAccountContext | null;
  capabilities: string[];
  systemRole: SystemRole;
  profileRole: ProfileRole | null;
  businessRole: BusinessRole | null;
  isSidebarCollapsed: boolean;
  isBootstrapping: boolean;
  hasBootstrapped: boolean;
  setTokens: (tokens: AuthTokens) => void;
  setAccessToken: (token: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  setIdentity: (identity: IdentityContext | null) => void;
  setAccountContext: (context: {
    identity?: IdentityContext | null;
    availableFunctionalAccounts?: FunctionalAccount[];
    activeContext?: ActiveAccountContext | null;
  }) => void;
  setActiveRoles: (roles: { systemRole?: SystemRole; profileRole?: ProfileRole | null; businessRole?: BusinessRole | null }) => void;
  setBootstrapState: (state: { isBootstrapping?: boolean; hasBootstrapped?: boolean }) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  logoutLocal: () => void;
}

/**
 * Lexon refresh token nga localStorage pa thyer renderimin server-side.
 */
export const getStoredRefreshToken = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Ruan refresh token perkohesisht derisa backend-i te kaloje ne HttpOnly cookie.
 */
export const storeRefreshToken = (token: string): void => {
  window.localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

/**
 * Fshin refresh token lokal pas logout ose refresh failure.
 */
export const clearStoredRefreshToken = (): void => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
};

const deriveLegacyRolesFromFunctionalAccountType = (
  functionalAccountType: FunctionalAccountType | null | undefined,
): { systemRole?: SystemRole; profileRole?: ProfileRole | null; businessRole?: BusinessRole | null } => {
  switch (functionalAccountType) {
    case "patient":
      return { systemRole: "patient", profileRole: "patient", businessRole: null };
    case "doctor":
      return { systemRole: "clinician", profileRole: "doctor", businessRole: null };
    case "specialist":
      return { systemRole: "clinician", profileRole: "specialist", businessRole: null };
    case "technician":
      return { systemRole: "clinician", profileRole: "technician", businessRole: null };
    case "business_owner":
      return { systemRole: "patient", profileRole: null, businessRole: "owner" };
    case "basic":
      return { systemRole: "patient", profileRole: null, businessRole: null };
    default:
      return {};
  }
};

export const useAuthStore = create<AuthStoreState>((set) => ({
  accessToken: null,
  user: null,
  identity: null,
  availableFunctionalAccounts: [],
  activeFunctionalAccount: null,
  activeOrganizationContext: null,
  capabilities: [],
  systemRole: "patient",
  profileRole: null,
  businessRole: null,
  isSidebarCollapsed: false,
  isBootstrapping: true,
  hasBootstrapped: false,
  setTokens: (tokens) => {
    storeRefreshToken(tokens.refreshToken);
    set({ accessToken: tokens.accessToken });
  },
  setAccessToken: (token) => set({ accessToken: token }),
  setUser: (user) => set({ user }),
  setIdentity: (identity) => set({ identity }),
  setAccountContext: ({ identity, availableFunctionalAccounts, activeContext }) =>
    set((state) => {
      const accounts = availableFunctionalAccounts ?? state.availableFunctionalAccounts;
      const activeFunctionalAccount =
        activeContext === undefined
          ? state.activeFunctionalAccount
          : accounts.find((account) => account.id === activeContext?.functional_account_id) ?? null;
      const activeOrganizationContext = activeContext === undefined ? state.activeOrganizationContext : activeContext;
      const derivedRoles = deriveLegacyRolesFromFunctionalAccountType(activeContext?.functional_account_type);
      const capabilities = activeContext?.capabilities ?? activeFunctionalAccount?.capabilities ?? state.capabilities;
      const nextSystemRole = derivedRoles.systemRole ?? state.systemRole;
      const nextProfileRole = derivedRoles.profileRole === undefined ? state.profileRole : derivedRoles.profileRole;
      const nextBusinessRole = derivedRoles.businessRole === undefined ? state.businessRole : derivedRoles.businessRole;

      return {
        identity: identity === undefined ? state.identity : identity,
        availableFunctionalAccounts: accounts,
        activeFunctionalAccount,
        activeOrganizationContext,
        capabilities,
        systemRole: nextSystemRole,
        profileRole: nextProfileRole,
        businessRole: nextBusinessRole,
        user: state.user
          ? {
              ...state.user,
              systemRole: nextSystemRole,
              profileRole: nextProfileRole,
              businessRole: nextBusinessRole,
              permissions: capabilities,
            }
          : state.user,
      };
    }),
  setActiveRoles: (roles) =>
    set((state) => ({
      systemRole: roles.systemRole ?? state.systemRole,
      profileRole: roles.profileRole === undefined ? state.profileRole : roles.profileRole,
      businessRole: roles.businessRole === undefined ? state.businessRole : roles.businessRole,
    })),
  setBootstrapState: (state) =>
    set((current) => ({
      isBootstrapping: state.isBootstrapping ?? current.isBootstrapping,
      hasBootstrapped: state.hasBootstrapped ?? current.hasBootstrapped,
    })),
  setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),
  toggleSidebar: () => set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
  logoutLocal: () => {
    clearStoredRefreshToken();
    if (typeof window !== "undefined") {
      window.localStorage.clear();
    }
    queryClient.clear();
    set({
      accessToken: null,
      user: null,
      identity: null,
      availableFunctionalAccounts: [],
      activeFunctionalAccount: null,
      activeOrganizationContext: null,
      capabilities: [],
      systemRole: "patient",
      profileRole: null,
      businessRole: null,
      isBootstrapping: false,
      hasBootstrapped: true,
    });
  },
}));
